/**
 * PAJ - Creating a Global Shared State using Context API
 * state defined in this AppContextProvider will be used throughout the context of our application
 */
import { FunctionComponent, useEffect, useState, useReducer } from 'react';
import { stripTrailingSlashes } from '@flos/react-components';
import { IAppProps, SCREEN_NAME } from '../interface/IAppProvider';
import { appContextReducer } from '../contextReducer/AppContextReducer';
import { AppStateContext, AppDispatchContext } from './AppContext';
import initialState from '../initialState/initialState';
import { removeSessionItem } from '../services/StorageService';
import { ROUTE_NAMES } from '../../router/RouterApp';
import { reloadApplicationStateFromCache } from '../actions/ObjectCacheActions';
import { track } from 'c9-tracking';
import { useLocation } from 'react-router-dom';

// string found in response from Hippo if content block cannot be found
const contentBlockNotFoundString = 'ERROR: Could not fetch the document for';

type HippoLabels = {
    url: string;
    bundle: string;
};

// To add more sections(resourcebundles), just add to this list
const sectionList = [
    'onsa.intro',
    'onsa.address',
    'onsa.customer_user',
    'onsa.household',
    'onsa.already_covered',
    'onsa.claim',
    'onsa.claims_cloud_burst',
    'onsa.compliance_v2',
    'onsa.age',
    'onsa.preconditions',
    'onsa.packages',
    'onsa.deductibles',
    'onsa.log_ind',
    'onsa.call_me',
    'onsa.basket',
    'onsa.about_you',
    'onsa.receipt',
    'onsa.termination',
    'onsa.error',
    'onsa.payment.betaling',
    'onsa.payment_options',
    'onsa.receipt.screen',
    'onsa.content.pricebox',
    'dk.onsa-common'
];

const contentBlocks: HippoLabels[] = [
    { url: 'onsa-content', bundle: 'addresspage_loggedin_hascontent_same' },
    { url: 'onsa-content', bundle: 'addresspage_not_in_bbr' },
    { url: 'onsa-content', bundle: 'packagepage_discount_overlay' },
    { url: 'onsa-content', bundle: 'rmo_hascontent_different' },
    { url: 'onsa-content', bundle: 'customer_or_user' },
    { url: 'onsa-content', bundle: 'legal-lines' },
    { url: 'onsa-content', bundle: 'legal-lines-logged-in' },
    { url: 'onsa-content', bundle: 'payment-service' },
    { url: 'onsa-content', bundle: 'termination-moving' },
    { url: 'onsa-content', bundle: 'rmo_generel_compliance' },
    { url: 'onsa-content', bundle: 'edi-text' },
    { url: 'common', bundle: 'customer-rejection' },
    { url: 'common', bundle: 'edi-overlay-text' },
    { url: 'onsa-content', bundle: 'fees-and-charges' }
];

const newValue = {
    type: SCREEN_NAME.INTRO,
    payload: { resourceBundles: null as any, contentBlocks: {} as any }
};

export const AppContextProvider: FunctionComponent<IAppProps> = (props) => {
    const [state, dispatch] = useReducer(appContextReducer, initialState);
    const [refresh, setRefresh] = useState(false);
    const [resources, setResources] = useState(false);
    const [trackReady, setTrackReady] = useState(false);
    const [backButtonClicked, setBackButtonClicked] = useState(false);
    const isReceipt = props.config.baseName.indexOf('kvittering') !== -1;
    const location = useLocation();

    useEffect(() => {
        const pathName = window.location.pathname;
        if (!trackReady) {
            setTrackReady(true);
            track.ready(true); //Initialize Track library for sas360 tracking
        }

        function getFullPathName(routeName: string, withLastBackSlash: boolean) {
            const baseNameWithoutLastBackSlash = stripTrailingSlashes(props.config.baseName);
            return withLastBackSlash
                ? `${baseNameWithoutLastBackSlash}${routeName}/`
                : `${baseNameWithoutLastBackSlash}${routeName}`;
        }

        function addSections(): Promise<any>[] {
            const callList: any = [];
            //filter onsa.intro. lad that up front
            sectionList.forEach((item) => {
                callList.push(getContentText(item));
            });
            return callList;
        }

        // Get the resource bundle by section
        function getContentText(section: string) {
            return fetch(`${props.config.resourceBundleApiUrl}/${section}`)
                .then((response) => {
                    if (response.ok) {
                        return response.json().then((json) => {
                            return { name: section, data: json };
                        });
                    } else {
                        throw new Error('response.ok was false');
                    }
                })
                .catch((err) => {
                    props.c9log.error(
                        `getContentText: URL:${props.config.resourceBundleApiUrl}/${section} section:${section} ${err}`,
                        'FRONT-END'
                    );
                    dispatch({ type: SCREEN_NAME.SET_ERROR, payload: true });
                });
        }
        /**
         * Fetch the resources for first page.
         * @param section
         */
        const fetchInitialResources = async () => {
            await getContentText('onsa.intro').then(
                (item: any) =>
                    item && item.name && item.data && newValue.payload.resourceBundles.set(item.name, item.data)
            );
            await getContentText('onsa.receipt.screen').then(
                (item: any) =>
                    item && item.name && item.data && newValue.payload.resourceBundles.set(item.name, item.data)
            );
            const contentBlockPromises = contentBlocks
                .filter(
                    (b) =>
                        b.bundle === 'customer-rejection' ||
                        b.bundle === 'addresspage_loggedin_hascontent_same' ||
                        b.bundle === 'addresspage_not_in_bbr'
                )
                .map((b) => {
                    return getContentBlock(b);
                });
            await Promise.all(contentBlockPromises).then((items: any) => {
                items.forEach((item: any) => {
                    item && (newValue.payload.contentBlocks[item.name] = item.data);
                });
            });
            dispatch(newValue);
            dispatch({ type: SCREEN_NAME.RESOURCES_READY, payload: true });
        };

        async function getContentBlock(section: HippoLabels) {
            const options = {
                method: 'GET',
                headers: {
                    'Content-Type': 'text/html'
                }
            };

            let path = 'shared/content-blocks/privat/selvbetjening/kobsflow/online-koeb/indbo';
            if (section.url === 'common') {
                path = 'shared/content-blocks/privat/selvbetjening/kobsflow/online-koeb/common';
            }
            const htmlContentPath = window.topContext.config.getEndpointConfig().htmlContent;
            return fetch(`${htmlContentPath}/?documentPath=${path}/${section.bundle}&template=nowrapper`, options)
                .then((response) => {
                    if (response.ok) {
                        return response.text().then((text) => {
                            const trimmed = text.trim();
                            const check = trimmed.startsWith(contentBlockNotFoundString);
                            if (!check) {
                                return {
                                    name: section.bundle,
                                    data: text
                                };
                            } else {
                                throw new Error('contentblock not found');
                            }
                        });
                    } else {
                        throw new Error('response.ok was false');
                    }
                })
                .catch((err) => {
                    props.c9log.error(
                        `getContentBlock URL: ${htmlContentPath}/?documentPath=${path}/${section.bundle}&template=nowrapper ${section.url} ${err}`,
                        'FRONT-END'
                    );
                    dispatch({
                        type: SCREEN_NAME.SET_ERROR,
                        payload: true
                    });
                });
        }

        function handleFlowComplete() {
            removeSessionItem('onsa-content-cache-key');
            dispatch({ type: SCREEN_NAME.EMAIL_SENT, payload: false });
            dispatch({ type: SCREEN_NAME.SET_SALES_COMPLETE, payload: false });
            dispatch({ type: SCREEN_NAME.SET_INITIAL_STATE, payload: true });
            //We don't care about the refresh since user completed one flow at this point
            //Use replace to clear the history
            window.location.replace(
                `${window.location.protocol}//${window.location.host}${getFullPathName(ROUTE_NAMES.INTRO, false)}`
            );
        }

        const fetchAllHippoContent = async () => {
            const callList: Promise<any>[] = addSections();
            Promise.all(callList)
                .then((respOne) => {
                    respOne.forEach((item: any) => {
                        if (item) {
                            newValue.payload.resourceBundles.set(item.name, item.data);
                        }
                    });

                    let callListCb: Promise<any>[] = [];
                    contentBlocks.forEach((cb) => {
                        let getCb = getContentBlock(cb);
                        callListCb.push(getCb);
                    });

                    Promise.all(callListCb).then((respTwo) => {
                        respTwo.forEach((item: any) => {
                            if (item) {
                                newValue.payload.contentBlocks[item.name] = item.data;
                            }
                        });
                        dispatch(newValue);
                        dispatch({ type: SCREEN_NAME.RESOURCES_READY, payload: true });
                    });
                })
                .catch((err) => {
                    throw new Error(err);
                });
        };

        //Browser back and forward
        window.onpopstate = () => {
            setBackButtonClicked(true);
        };

        //Handle refresh
        if (!state.refreshFromCache && !refresh) {
            setRefresh(true);
            //Check user route to / or intro page
            if (
                !isReceipt &&
                (pathName === getFullPathName(ROUTE_NAMES.INTRO, false) ||
                    pathName === props.config.baseName ||
                    pathName === getFullPathName(ROUTE_NAMES.INTRO, true))
            ) {
                // remove session on reload of page
                removeSessionItem('onsa-content-cache-key');
            }
            reloadApplicationStateFromCache(props, dispatch);
            dispatch({ type: SCREEN_NAME.RESOURCES_READY, payload: false });
        }

        if (
            backButtonClicked &&
            (pathName === getFullPathName(ROUTE_NAMES.INTRO, false) ||
                pathName === props.config.baseName ||
                pathName === getFullPathName(ROUTE_NAMES.INTRO, true))
        ) {
            setBackButtonClicked(false);
            dispatch({ type: SCREEN_NAME.RESOURCES_READY, payload: true });
        }

        if (
            (state.emailSent &&
                pathName !== getFullPathName(ROUTE_NAMES.EXIT, false) &&
                pathName !== getFullPathName(ROUTE_NAMES.EXIT, true)) ||
            (state.salesComplete && !isReceipt)
        ) {
            handleFlowComplete();
        }

        if (!state.resourcesReady && state.refreshFromCache && !resources) {
            setResources(true);
            newValue.payload.resourceBundles = new Map<string, any>();
            //if on the first page. fetch that content first. Then fetch the rest async.
            if (location.pathname === '/') {
                fetchInitialResources().then(() => {
                    fetchAllHippoContent();
                });
            } else {
                fetchAllHippoContent();
            }
        }
    }, [
        props,
        refresh,
        resources,
        dispatch,
        state.resourcesReady,
        state.refreshFromCache,
        state.emailSent,
        state.salesComplete,
        backButtonClicked,
        state.section,
        state.initialState,
        trackReady,
        isReceipt,
        state.contentBlocks,
        location.pathname
    ]);

    return (
        <AppStateContext.Provider value={state}>
            <AppDispatchContext.Provider value={dispatch}>{props.children}</AppDispatchContext.Provider>
        </AppStateContext.Provider>
    );
};
